import { cn } from "@apex/shared";
import type { ViewType } from "../state";

/** A single step in the wizard. */
export interface StepperStep {
  /** Maps to a {@link ViewType} for navigation purposes. */
  id: ViewType;
  /** Human-readable label rendered in the stepper. */
  label: string;
  /** Whether the step can currently be navigated to. Unreachable steps are visually muted and ignore click. */
  reachable: boolean;
  /** Whether the user has progressed past this step. Completed steps render a check icon. */
  completed: boolean;
}

export interface StepperProps {
  /** Steps to render, in order (length === 3 expected for v1 wizard). */
  steps: StepperStep[];
  /** Id of the currently active step. */
  activeStep: ViewType;
  /** Callback when a reachable step is clicked. Unreachable steps do not fire this. */
  onStepClick: (id: ViewType) => void;
  /** Optional class for the wrapper. */
  className?: string;
}

function CheckCircle({ size = 14 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * Stepper — renders a horizontal 3-step progress indicator on desktop and a
 * compact dot layout on mobile. Active step uses the tenant primary brand
 * colour; completed steps show a check icon; unreachable steps are visually
 * muted and ignore clicks.
 *
 * Accessibility: wrapped in a `role="navigation"` landmark with
 * `aria-label="Wizard progress"`. Each step is a real `<button>` (touch
 * target ≥ 44×44 on mobile) and the active step carries `aria-current="step"`.
 *
 * Hidden by the parent for `loading`, `success`, and `error` views.
 */
export function Stepper({ steps, activeStep, onStepClick, className }: StepperProps) {
  const activeIndex = steps.findIndex((s) => s.id === activeStep);

  function handleClick(step: StepperStep) {
    if (!step.reachable) return;
    onStepClick(step.id);
  }

  return (
    <nav
      aria-label="Wizard progress"
      className={cn(
        "apex-stepper apex-w-full apex-shrink-0 apex-border-b apex-border-border apex-bg-background",
        className
      )}
    >
      {/* Mobile (< md): compact layout — dots + current label */}
      <div data-stepper-variant="mobile" className="apex-flex apex-flex-col apex-items-center apex-gap-1.5 apex-px-4 apex-py-3 md:apex-hidden">
        <div className="apex-flex apex-items-center apex-gap-2" role="presentation">
          {steps.map((step, index) => {
            const isActive = step.id === activeStep;
            const isPast = index < activeIndex || step.completed;
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => handleClick(step)}
                aria-current={isActive ? "step" : undefined}
                aria-disabled={!step.reachable}
                aria-label={`Step ${index + 1} of ${steps.length}: ${step.label}`}
                className={cn(
                  "apex-h-11 apex-w-11 apex-rounded-full apex-flex apex-items-center apex-justify-center",
                  "apex-text-xs apex-font-semibold apex-transition-colors",
                  "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-1",
                  isActive
                    ? "apex-bg-primary apex-text-primary-foreground"
                    : isPast
                    ? "apex-bg-primary/15 apex-text-primary"
                    : "apex-bg-muted apex-text-muted-foreground",
                  !step.reachable && !isActive && "apex-opacity-60 apex-cursor-not-allowed"
                )}
              >
                {step.completed && !isActive ? <CheckCircle /> : index + 1}
              </button>
            );
          })}
        </div>
        <p className="apex-text-xs apex-text-muted-foreground apex-font-medium">
          Step {activeIndex + 1} of {steps.length}
          {steps[activeIndex] ? ` · ${steps[activeIndex].label}` : ""}
        </p>
      </div>

      {/* Desktop (≥ md): horizontal layout with progress lines */}
      <div data-stepper-variant="desktop" className="apex-hidden md:apex-flex apex-items-center apex-gap-2 apex-px-6 apex-py-3">
        {steps.map((step, index) => {
          const isActive = step.id === activeStep;
          const isPast = index < activeIndex || step.completed;
          const isLast = index === steps.length - 1;

          return (
            <div key={step.id} className="apex-flex apex-items-center apex-flex-1 apex-min-w-0">
              <button
                type="button"
                onClick={() => handleClick(step)}
                aria-current={isActive ? "step" : undefined}
                aria-disabled={!step.reachable}
                disabled={!step.reachable && !isActive}
                className={cn(
                  "apex-flex apex-items-center apex-gap-2 apex-min-h-[44px] apex-px-2 apex-py-1.5 apex-rounded-md",
                  "apex-text-sm apex-font-medium apex-transition-colors",
                  "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-1",
                  step.reachable
                    ? "hover:apex-bg-muted apex-cursor-pointer"
                    : "apex-cursor-not-allowed",
                  !step.reachable && !isActive && "apex-opacity-50"
                )}
              >
                <span
                  className={cn(
                    "apex-h-7 apex-w-7 apex-rounded-full apex-flex apex-items-center apex-justify-center apex-text-xs apex-font-semibold apex-shrink-0",
                    isActive
                      ? "apex-bg-primary apex-text-primary-foreground"
                      : isPast
                      ? "apex-bg-primary/20 apex-text-primary"
                      : "apex-bg-muted apex-text-muted-foreground"
                  )}
                  aria-hidden="true"
                >
                  {isPast && !isActive ? <CheckCircle /> : index + 1}
                </span>
                <span
                  className={cn(
                    "apex-truncate",
                    isActive ? "apex-text-foreground" : "apex-text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </button>
              {!isLast && (
                <div
                  aria-hidden="true"
                  className={cn(
                    "apex-flex-1 apex-h-px apex-mx-2",
                    index < activeIndex ? "apex-bg-primary/40" : "apex-bg-border"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
